import { useEffect, useState, type ReactNode } from "react";
import { FileText, LayoutDashboard, LogOut, Menu, Settings, X } from "lucide-react";
import { api } from "./api";
import { Brand } from "./components/Brand";
import { ThemeToggle } from "./components/ThemeToggle";
import { DashboardPage } from "./pages/DashboardPage";
import { EditorPage } from "./pages/EditorPage";
import { LoginPage } from "./pages/LoginPage";
import { PublicPage } from "./pages/PublicPage";
import { SettingsPage } from "./pages/SettingsPage";
import { Link, navigate, usePath } from "./router";

function AdminShell({ path, children, onLogout }: { path: string; children: ReactNode; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  return <div className="admin-shell">
    <header className="admin-mobile-header"><Brand compact /><div><ThemeToggle compact /><button type="button" onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"}>{open ? <X /> : <Menu />}</button></div></header>
    <aside className={`admin-sidebar ${open ? "admin-sidebar--open" : ""}`}>
      <Brand />
      <nav>
        <Link className={path === "/admin" ? "active" : ""} href="/admin"><LayoutDashboard />Dashboard</Link>
        <Link className={path.startsWith("/admin/proposals") ? "active" : ""} href="/admin"><FileText />Proposals</Link>
        <Link className={path === "/admin/settings" ? "active" : ""} href="/admin/settings"><Settings />Settings</Link>
      </nav>
      <div className="sidebar-note"><strong>Proposal Studio</strong><p>Create, publish and track every client proposal.</p></div>
      <ThemeToggle />
      <button className="sidebar-logout" type="button" onClick={onLogout}><LogOut />Sign out</button>
    </aside>
    <div className="admin-content">{children}</div>
  </div>;
}

export default function App() {
  const path = usePath();
  const isAdminPath = path === "/" || path === "/login" || path.startsWith("/admin");
  const [authenticated, setAuthenticated] = useState<boolean | null>(isAdminPath ? null : false);

  useEffect(() => {
    if (!isAdminPath) return;
    void api<{ authenticated: boolean }>("/api/session").then((result) => setAuthenticated(result.authenticated)).catch(() => setAuthenticated(false));
  }, [isAdminPath]);

  if (!isAdminPath) return <PublicPage slug={decodeURIComponent(path.slice(1))} />;
  if (authenticated === null) return <div className="public-loading"><span className="brand-mark"><span /></span><p>Opening workspace…</p></div>;
  if (!authenticated) return <LoginPage />;

  if (path === "/" || path === "/login") {
    window.history.replaceState({}, "", "/admin");
  }
  const currentPath = window.location.pathname;
  const editorMatch = currentPath.match(/^\/admin\/proposals\/([a-f0-9-]+)$/);
  const logout = async () => { await api("/api/logout", { method: "POST", body: "{}" }); setAuthenticated(false); navigate("/login"); };
  const page = editorMatch ? <EditorPage id={editorMatch[1]} /> : currentPath === "/admin/settings" ? <SettingsPage /> : <DashboardPage />;
  return <AdminShell path={currentPath} onLogout={() => void logout()}>{page}</AdminShell>;
}
